import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Filter, Loader2, Inbox, AlertTriangle, PlayCircle, RefreshCw,
  Search, HeartPulse, LineChart, Handshake, ChevronDown, Info, X, Check, Radio, Flame,
} from "lucide-react";
import { SocialLogo, SUPPORTED_PLATFORMS } from "@/components/landing/SocialLogos";
import { FUNNEL_STAGE_ORDER, FUNNEL_STAGE_META, isPainPost } from "@/lib/funnelStage";

// id -> display name for the scan-modal platform picker.
const PLATFORM_NAMES = Object.fromEntries(SUPPORTED_PLATFORMS.map((p) => [p.id, p.name]));

// 3 parent features now — Pain Points isn't a separate lens/scan anymore, it's
// a filter INSIDE Find Customers (same fetched data, same scan — see the
// "Pain & Complaints" toggle below). Market Insights + Find Partners are
// COMING SOON — their UI and logic need a different shape.
const LENSES = [
  { id: "find_customers", label: "Customers", emoji: "🔥", icon: Search },
  { id: "market", label: "Market Insights", emoji: "📊", icon: LineChart, comingSoon: true },
  { id: "partners", label: "Find Partners", emoji: "🤝", icon: Handshake, comingSoon: true },
];

// Which lenses are live right now. Coming-soon lenses can't be viewed or scanned.
const ACTIVE_LENSES = new Set(LENSES.filter((l) => !l.comingSoon).map((l) => l.id));
const isLensActive = (id) => ACTIVE_LENSES.has(id);

// Full lens display names (the LENS level). Results inside a lens are grouped
// by FUNNEL STAGE (how close to buying) — a lens is the parent, a stage is
// the child. Keep the two levels visually distinct.
const LENS_FULL_NAME = {
  find_customers: "Find Customers",
  market: "Market Insights",
  partners: "Find Partners",
};

// Results are grouped into one section per funnel stage — ordered by buying
// momentum (ready > comparing > solution_aware > problem_aware > post_purchase,
// matching the AI's own priority ordering). "unaware" never appears here (it's
// excluded from the find_customers tag entirely) — a post at that stage only
// surfaces via the Pain & Complaints toggle.
const STAGES = FUNNEL_STAGE_ORDER.map((id) => ({ id, ...FUNNEL_STAGE_META[id] }));
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/lib/testIds";
import LeadCard from "@/components/LeadCard";
import ScanLoader from "@/components/ScanLoader";
import PaywallModal from "@/components/PaywallModal";
import RequiresBrand from "@/components/RequiresBrand";
// Developer API disabled (coming soon):
// import CopyAsCurl from "@/components/CopyAsCurl";
import FirstScanSafetyNet from "@/components/FirstScanSafetyNet";
import EmptyScanPanel from "@/components/EmptyScanPanel";
import { isQuotaError, extractPaywallInfo, isUpgradeError, upgradeMessage } from "@/lib/paywall";
import { useEntitlements } from "@/lib/useEntitlements";
import {
  freeScansPhrase,
  freeScanUsedPhrase,
  scansLeftSentence,
  scansRemaining,
} from "@/lib/planCopy";
import { UpgradeBanner } from "@/components/UpgradeLock";

/**
 * Thrown by `pollScan` / `applyScanResult` when the TENANT changed underneath
 * them — a workspace switch or a brand switch. Both are the same event here:
 * a scan belongs to one brand in one workspace, and either changing makes its
 * payload wrong for the screen.
 *
 * A sentinel object rather than an Error subclass so `catch` sites can compare
 * by identity and cannot mistake it for a real failure. It must NEVER reach the
 * user: "Scan failed" after switching would be a lie about the new tenant, and
 * the scan it refers to is still running perfectly well in the old one. Every
 * catch site checks for it first and returns quietly.
 */
const TENANT_CHANGED = Symbol("tenant-changed");

export default function FindCustomers() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    workspace,
    tenantEpoch,
    refreshWorkspace,
    brand: ctxBrand,
    brandLoading,
    loading: authLoading,
    reportBrandGone,
  } = useAuth();

  // Phase 5.5 / B3 — the epoch, readable from inside a running poll.
  //
  // `pollScan` runs up to 160 iterations at 3s, so a scan started on brand A
  // can still be in its loop EIGHT MINUTES later, long after the user moved to
  // B. Before the epoch there was no cancellation inside that loop at all: it
  // kept requesting, and on completion it wrote A's summary, A's safety net and
  // A's empty-scan explanation into the screen now labelled B — or surfaced A's
  // scan error as if B had failed. The `cancelled` flags on the surrounding
  // effects did not help, for two independent reasons: the loop is not the
  // effect, and a mount-cancel flag never fires at all when the component stays
  // mounted and only the brand changes underneath it.
  //
  // This is now the TENANT epoch, so a brand switch cancels the loop exactly as
  // a workspace switch does. That is the whole reason for one counter.
  //
  // A ref, not the state value: the loop closes over its variables once, at
  // start. Reading `tenantEpoch` directly would read the value captured at that
  // moment, forever, which is exactly the bug. The ref is a live window.
  const epochRef = useRef(tenantEpoch);
  epochRef.current = tenantEpoch;

  const brand = ctxBrand;
  // Live window on the seated brand, for the same reason as `epochRef`: every
  // async continuation below needs to ask "is this STILL the brand I started
  // for?", and the answer must come from now, not from closure-capture time.
  const brandRef = useRef(brand);
  brandRef.current = brand;

  /**
   * The one guard every await-then-setState uses. Callers capture
   * `[epochRef.current, brand?.brand_id]` before their first request and call
   * this after every await, before every write to state.
   *
   * Both halves are load-bearing. The epoch alone would miss nothing today, but
   * it is a counter and a brand id is the actual identity of the data; checking
   * the id as well means a write can only land on the brand it was fetched for
   * even if some future path forgets to bump. Belt and braces, deliberately.
   */
  const stillOurs = useCallback(
    (epoch, brandId) =>
      epochRef.current === epoch &&
      (brandId === undefined || brandRef.current?.brand_id === brandId),
    []
  );
  const brandsLoading = authLoading || brandLoading;
  // `entLoaded` false = we don't know this workspace's plan yet. Everything
  // else here reads as "free / used up" until it lands (the hook fails closed),
  // so nothing plan-dependent may render or fire before it's true.
  const { loaded: entLoaded, unknown: entUnknown, isPaid, scanUsed } = useEntitlements();

  const initialLens = new URLSearchParams(location.search).get("lens");
  const [activeLens, setActiveLens] = useState(
    isLensActive(initialLens) ? initialLens : "find_customers"
  );
  // Keep the active lens in sync with the URL (?lens=) so sidebar sub-links work.
  // Coming-soon lenses (market / partners) fall back to Find Customers.
  useEffect(() => {
    const l = new URLSearchParams(location.search).get("lens");
    setActiveLens(isLensActive(l) ? l : "find_customers");
  }, [location.search]);

  // Active (ticked) keywords for the scan-confirm modal + lens the modal will scan.
  const [activeKeywords, setActiveKeywords] = useState([]);
  const [scanLens, setScanLens] = useState("find_customers");

  // Platform picker (scan modal). platformOpts = [{id, suggested}]; selectedPlatforms
  // = Set of ticked ids (suggested pre-ticked, user-editable). null = not loaded yet.
  // Capped at MAX_PLATFORMS per scan — more platforms = a much slower scan.
  const MAX_PLATFORMS = 5;
  const [platformOpts, setPlatformOpts] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState(null);
  const togglePlatform = useCallback((id) => {
    setSelectedPlatforms((prev) => {
      const cur = prev || new Set();
      const next = new Set(cur);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= MAX_PLATFORMS) return cur; // at cap — ignore extra picks
        next.add(id);
      }
      return next;
    });
  }, []);

  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState(null);

  const [scanning, setScanning] = useState(false);
  const [askCost, setAskCost] = useState(false);
  const [scanSummary, setScanSummary] = useState(null);
  const [lastScanId, setLastScanId] = useState(null);
  const [scanStartedAt, setScanStartedAt] = useState(null); // ms — for a persistent loader timer
  const [safetyNet, setSafetyNet] = useState(null);
  // Why the latest scan came back empty — the persistent replacement for the
  // toast that used to say it once and expire. Shape mirrors the poll payload:
  // { reason, count, refunded, refundDeclined, refundsPerDay, feature }.
  // `reason` is `empty_reason`; nothing here is ever derived from `all_seen`.
  const [emptyScan, setEmptyScan] = useState(null);
  const [paywall, setPaywall] = useState(null);

  const [filterPlatform, setFilterPlatform] = useState("");
  const [showPainOnly, setShowPainOnly] = useState(false);
  // Intel/broadcast content (creator posts, directory listings — not a 1:1 buyer
  // conversation) is hidden from the main buyer view by default (doc 07: "never
  // diluting the hot-leads view") and only shown via this toggle.
  const [showIntelOnly, setShowIntelOnly] = useState(false);
  const [minScore, setMinScore] = useState(0);

  const [savedResultIds, setSavedResultIds] = useState(new Set());
  const [savingIds, setSavingIds] = useState(new Set());
  const [collapsed, setCollapsed] = useState({}); // stage id -> bool

  // Which results are already saved. Tenant-derived (`result_id`s belong to the
  // workspace that produced them), so it is keyed on the epoch — it used to be
  // keyed on `[navigate]`, i.e. effectively mount-only, which meant that after a
  // switch the bookmark icons were the PREVIOUS tenant's answers to "have I
  // saved this?" drawn over the new tenant's rows.
  //
  // B4 — `/leads` is sent WITH `brand_id` now. It was fetched unscoped, so in a
  // multi-brand workspace the bookmark icons were the union of every brand's
  // saved leads: a result saved under brand A showed as already-saved while
  // browsing brand B. The endpoint has always accepted the filter; nothing was
  // passing it.
  useEffect(() => {
    if (!brand) {
      setSavedResultIds(new Set()); // fail closed — never another brand's answers
      return;
    }
    const epoch = epochRef.current;
    const brandId = brand.brand_id;
    (async () => {
      try {
        const r = await api.get("/leads", { params: { brand_id: brandId } });
        if (!stillOurs(epoch, brandId)) return; // checked AFTER the await, not before
        const ids = new Set((r.data?.items || []).map((l) => l.result_id));
        setSavedResultIds(ids);
      } catch (e) {
        if (!stillOurs(epoch, brandId)) return;
        // B6 — the brand this list is about is gone. Hand it to AuthContext,
        // which clears the seat, re-derives and raises the notice.
        if (e?.response?.status === 404) reportBrandGone?.(brandId);
      }
    })();
  }, [brand, tenantEpoch, stillOurs, reportBrandGone]);

  // Load the brand's ticked keywords (shown in the scan-confirm modal).
  useEffect(() => {
    if (!brand) {
      setActiveKeywords([]); // fail closed: never offer brand A's keywords for B
      return;
    }
    const epoch = epochRef.current;
    const brandId = brand.brand_id;
    api.get(`/brands/${brandId}`)
      .then((r) => {
        if (!stillOurs(epoch, brandId)) return;
        setActiveKeywords((r.data?.keywords || []).filter((k) => k.default_selected !== false));
      })
      .catch(() => {});
  }, [brand, stillOurs]);

  // When the scan modal opens (or the lens changes while open), load the
  // platform options for that lens and pre-tick the suggested ones. The user
  // can then tick/untick freely before running.
  useEffect(() => {
    if (!askCost || !brand) return;
    let cancelled = false;
    const epoch = epochRef.current;
    const brandId = brand.brand_id;
    api.get("/scans/platform-options", { params: { brand_id: brandId, feature: scanLens } })
      .then((r) => {
        // `cancelled` covers unmount/re-run; `stillOurs` covers the case it
        // cannot see — the tenant moving while this component stays mounted.
        if (cancelled || !stillOurs(epoch, brandId)) return;
        const opts = r.data?.options || [];
        setPlatformOpts(opts);
        // Pre-tick the suggested platforms, capped at MAX_PLATFORMS.
        setSelectedPlatforms(new Set(opts.filter((o) => o.suggested).map((o) => o.id).slice(0, MAX_PLATFORMS)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [askCost, scanLens, brand, stillOurs]);

  // Results ARE the tenant-derived numbers on this screen, so this is the write
  // that matters most. Identity is captured before the request and re-checked
  // after it: a `/results` response for brand A that lands after the user moved
  // to B is dropped on the floor, including its error.
  const fetchResults = useCallback(async (b, feature = activeLens) => {
    if (!b) return;
    const epoch = epochRef.current;
    const brandId = b.brand_id;
    setResultsLoading(true);
    setResultsError(null);
    try {
      const r = await api.get("/results", {
        params: {
          brand_id: brandId,
          feature,
          platform: filterPlatform || undefined,
          min_score: minScore || 0,
          // no limit — return all results for this lens
        },
      });
      if (!stillOurs(epoch, brandId)) return;
      setResults(r.data?.items || []);
    } catch (e) {
      // A failure that belongs to a tenant the user has left is not news. It
      // must not become "Could not load results." over the new tenant's screen.
      if (!stillOurs(epoch, brandId)) return;
      // B6 — a 404 on a brand-scoped read means the BRAND is gone (deleted
      // here, in another tab, or by /brands/reset), not that results failed to
      // load. "Could not load results." would send the user to retry a screen
      // that can never succeed, so this is routed to the one handler that
      // clears the seat, re-derives, and says what happened.
      if (e?.response?.status === 404) {
        reportBrandGone?.(brandId);
        return;
      }
      setResultsError(e?.response?.data?.detail || "Could not load results.");
    } finally {
      // Also guarded: a superseded fetch resolving late would otherwise clear
      // the loading flag that the CURRENT fetch had just set. The tenant-clear
      // effect below owns resetting this on a switch.
      if (stillOurs(epoch, brandId)) setResultsLoading(false);
    }
  }, [filterPlatform, minScore, activeLens, stillOurs, reportBrandGone]);

  useEffect(() => {
    if (brand) fetchResults(brand);
  }, [brand, fetchResults]);

  // Clear everything tenant-derived the moment the tenant changes — workspace
  // OR brand, which is why this keys on `tenantEpoch`.
  //
  // Not redundant with the effect above. That one refetches when a brand
  // ARRIVES; it does nothing when the new workspace has no brand at all, which
  // is the common case for a freshly created one. Without this, switching into
  // an empty workspace leaves the previous tenant's leads, scan summary and
  // empty-scan explanation sitting on screen under the new name — a wrong
  // number rendered with total confidence. Render nothing instead.
  //
  // `switchBrand` bumps the epoch in the same commit as it nulls the brand, so
  // this runs on the very first render of the transition. There is no frame in
  // which brand A's summary is on screen under brand B's name.
  // Guarded by comparing the epoch rather than "skip the first run" — the
  // latter is not idempotent and StrictMode's double-invoke turned it into "run
  // the reset on mount", clearing state nothing had changed. See the same fix,
  // and the fuller argument, in Onboarding.jsx.
  const lastResetEpoch = useRef(tenantEpoch);
  useEffect(() => {
    if (lastResetEpoch.current === tenantEpoch) return;
    lastResetEpoch.current = tenantEpoch;
    setResults([]);
    setResultsError(null);
    setResultsLoading(false); // superseded fetches skip their own finally
    setSavedResultIds(new Set()); // result_ids belong to the tenant that made them
    setActiveKeywords([]);
    setPlatformOpts([]);
    setSelectedPlatforms(null);
    setScanSummary(null);
    setSafetyNet(null);
    setEmptyScan(null);
    setPaywall(null);
    setLastScanId(null);
    setScanStartedAt(null);
    setScanning(false);
  }, [tenantEpoch]);

  // Poll a running scan (async delivery) until it completes; returns the payload.
  //
  // Cancellation is by TENANT EPOCH + brand id, taken as arguments rather than
  // read from a closure, and checked at the top of every iteration and again
  // after every await. MAX_TRIES = 160 at 3s is an eight-minute window; the
  // whole defect was that a brand or workspace change anywhere inside it still
  // ended in a write. A switch mid-scan now ends the loop immediately and
  // silently — see TENANT_CHANGED above for why silence is the right answer.
  const pollScan = useCallback(async (scanId, epoch, brandId) => {
    const POLL_MS = 3000, MAX_TRIES = 160; // ~8 min safety cap
    for (let i = 0; i < MAX_TRIES; i++) {
      if (!stillOurs(epoch, brandId)) throw TENANT_CHANGED;
      let s;
      try {
        s = await api.get(`/scans/${scanId}/status`);
      } catch (e) {
        // B6, and this loop is the worst case of it: ~8 minutes of polling a
        // scan whose brand was deleted in another tab. Before this, the loop
        // simply kept going and eventually surfaced a scan error about a brand
        // that no longer exists. A 404 here ends the poll and reports the brand
        // gone; TENANT_CHANGED is the right signal because, from this loop's
        // point of view, the tenant it was running for has indeed ended.
        if (e?.response?.status === 404 && stillOurs(epoch, brandId)) {
          reportBrandGone?.(brandId);
          throw TENANT_CHANGED;
        }
        throw e;
      }
      if (!stillOurs(epoch, brandId)) throw TENANT_CHANGED;
      if (s.data?.status === "completed") return s.data;
      if (s.data?.status === "error") throw new Error(s.data?.error_message || "Scan failed.");
      await new Promise((res) => setTimeout(res, POLL_MS));
      if (!stillOurs(epoch, brandId)) throw TENANT_CHANGED; // the 3s sleep is an await too
    }
    throw new Error("Scan is taking longer than usual — check back in a moment.");
  }, [stillOurs, reportBrandGone]);

  // Apply a finished scan's payload: summary, safety net, results (+ optional toast).
  //
  // The identity comes in as arguments. It used to close over `brand` and call
  // `fetchResults(brand, feature)` with whatever `brand` happened to be when
  // this callback was BUILT — so a scan that finished after a brand change
  // wrote A's summary, A's safety net and A's empty-scan explanation into the
  // screen labelled B, then fetched A's results to sit under them. The dep
  // array cannot fix that: the callback in flight is the old one, and rebuilding
  // it does not retroactively change the closure the running scan is inside.
  const applyScanResult = useCallback(async (data, feature, { toast: showToast = true, epoch, brandId } = {}) => {
    // Nothing below this line may run for a tenant we have left.
    if (!stillOurs(epoch, brandId)) throw TENANT_CHANGED;
    setScanSummary(data.summary || null);
    setSafetyNet(data.safety_net || null);
    // Branch on `empty_reason`, NEVER on `all_seen`. `all_seen` is
    // `fetched > 0 && new == 0`, which is ALSO true for `all_filtered` — that
    // single flag speaking for two different outcomes is what made this screen
    // tell people "No new signals since your last scan" after a scan that read
    // hundreds of posts and set every one aside.
    setEmptyScan(
      data.empty_reason
        ? {
            reason: data.empty_reason,
            count: data.empty_count,
            refunded: !!data.action_refunded,
            refundDeclined: data.refund_declined_reason || null,
            refundsPerDay: data.empty_scan_refunds_per_day,
            feature,
          }
        : null
    );
    await refreshWorkspace();
    // Re-checked AFTER the await. `refreshWorkspace` runs a retry ladder that
    // can take ~17s on a bad network — plenty of time for a switch, after which
    // a "Found 12 new leads" toast would be a claim about the wrong brand.
    if (!stillOurs(epoch, brandId)) throw TENANT_CHANGED;
    if (showToast) {
      // Only the SUCCESS path toasts. On the success path the new leads land
      // below the fold and the toast is the only thing that says how many
      // arrived; on the empty path the explanation is the entire content of the
      // results area the user is already looking at, and EmptyScanPanel is a
      // polite live region, so a toast would be the same sentence twice — one
      // copy of which expires. Two surfaces for one fact is how the old wording
      // drifted out of sync with reality in the first place.
      const gotNew = data.summary?.new || 0;
      const dropped = data.summary?.dropped_total || 0;
      if (gotNew > 0) {
        toast.success(dropped > 0
          ? `Found ${gotNew} new leads. Filtered ${dropped} junk posts so you don't have to.`
          : `Found ${gotNew} new leads.`);
      }
    }
    // `brandRef.current`, not the captured `brand` — and only after the check
    // above has proved it is the same brand this scan was started for.
    await fetchResults(brandRef.current, feature);
  }, [refreshWorkspace, fetchResults, stillOurs]);

  // Persistent loader: on brand load, if the newest scan is still running,
  // resume the spinner + poll to completion. Survives refresh because "running"
  // comes from the DB (the scan record), not component state.
  useEffect(() => {
    if (!brand || scanning) return;
    let cancelled = false;
    // Captured once, at the top, and re-checked after every single await below.
    // The `cancelled` flag stays because it handles unmount and effect re-run,
    // but it is NOT sufficient on its own: this component survives a brand
    // change, so there are paths where the effect never re-runs in time and
    // `cancelled` is still false while the tenant has moved.
    const epoch = epochRef.current;
    const brandId = brand.brand_id;
    const ours = () => !cancelled && stillOurs(epoch, brandId);
    (async () => {
      try {
        const r = await api.get("/scans", { params: { brand_id: brandId, limit: 1 } });
        const latest = (r.data?.items || [])[0];
        if (!latest || !ours()) return;
        // Refresh-resume for the empty explanation. A user who reloads the page
        // a minute after an empty scan must still be able to read WHY the screen
        // is empty — the fact lives on the scan document, so it survives.
        // Re-poll the status endpoint (rather than reading the list row) purely
        // so the charged line can quote the real `empty_scan_refunds_per_day`;
        // that value is a server constant and is not on the document.
        if (latest.status === "completed" && latest.empty_reason) {
          try {
            const s = await api.get(`/scans/${latest.scan_id}/status`);
            if (!ours()) return;
            setEmptyScan({
              reason: s.data?.empty_reason,
              count: s.data?.empty_count,
              refunded: !!s.data?.action_refunded,
              refundDeclined: s.data?.refund_declined_reason || null,
              refundsPerDay: s.data?.empty_scan_refunds_per_day,
              feature: latest.feature || activeLens,
            });
          } catch (_) { /* no explanation available — fall back to the generic empty state */ }
        }
        if (latest.status !== "running") return;
        setScanning(true);
        setLastScanId(latest.scan_id);
        // Use the scan's real start time so the timer resumes, not restarts.
        setScanStartedAt(latest.started_at ? new Date(latest.started_at).getTime() : Date.now());
        const data = await pollScan(latest.scan_id, epoch, brandId);
        if (!ours()) return;
        await applyScanResult(data, latest.feature || activeLens, { toast: false, epoch, brandId });
      } catch (e) {
        if (e === TENANT_CHANGED) return; // the effect re-runs for the new tenant
        /* stop spinner on failure */
      } finally {
        // Guarded by identity, not just by `cancelled`: leaving the spinner
        // decision to a stale continuation would let brand A's finished poll
        // switch off a spinner that belongs to brand B's running one.
        if (ours()) setScanning(false);
      }
    })();
    return () => { cancelled = true; };
    // Keyed on brand_id, NOT the brand object: `brand` comes from AuthContext and
    // gets a new identity on every refreshBrand() even when it is the same brand,
    // which re-ran this resume probe for no reason.
    //
    // The suppression stays, and must. The other "missing" deps are all unsafe:
    //   · `scanning` — this effect CALLS setScanning(true). Listing it makes the
    //     effect cancel the very poll it just started, every time.
    //   · `activeLens` — only a fallback label for the resumed scan. Listing it
    //     restarts the resume poll each time the user clicks a lens tab.
    //   · `applyScanResult` / `pollScan` — useCallbacks over brand + fetchResults;
    //     their identity churns and would restart the poll mid-scan.
    //
    // `tenantEpoch` is listed, as the Phase 5 note here predicted: it is the one
    // dependency that legitimately needs to restart the poll. Note that it does
    // double duty — the dep re-runs the resume probe for the new tenant, and
    // `pollScan`'s own epoch check kills the OLD tenant's loop, which the dep
    // array alone cannot do because the loop is not the effect.
    //
    // `stillOurs` is stable (useCallback with [] deps over two refs), so it is
    // safe to omit and would not re-run this if listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.brand_id, tenantEpoch]);

  const runScan = async (feature = activeLens) => {
    if (!brand) return;
    // Coming-soon lenses can't be scanned yet.
    if (!isLensActive(feature)) {
      toast.info(`${LENS_FULL_NAME[feature] || "This lens"} is coming soon.`);
      return;
    }
    // Don't judge the plan before we know it — otherwise a paid user who gets
    // a click in during the load window is told to upgrade to something they
    // already own. The trigger is disabled until then, so this is belt-and-braces.
    if (!entLoaded) return;
    // Free-tier lens gate. This one stays client-side: "a free plan cannot use
    // other lenses" is a stable property of the plan, not a counter that moves
    // while the tab is open, so it cannot go stale between fetches.
    if (!isPaid && feature !== "find_customers") {
      toast.error("This lens is a paid feature. Upgrade to unlock all lenses.");
      navigate("/app/billing");
      return;
    }
    // NOTE: we deliberately do NOT pre-check `scanUsed` here.
    //
    // It is a mutable counter cached per tab with no cross-tab sync, so a tab
    // open for a while could refuse a scan the user actually still had — and a
    // second tab, freshly loaded, would disagree with the first. `guard_scan`
    // in backend/entitlements.py is the real authority and returns its own
    // message; the catch below renders that. Losing one round-trip on a refused
    // scan is worth never lying about whose scan it is.
    setAskCost(false);
    // If scanning a different lens than we're viewing, switch the view to match.
    if (feature !== activeLens) navigate(`/app/find-customers?lens=${feature}`);
    setScanning(true);
    setScanSummary(null);
    setSafetyNet(null);
    // The previous scan's explanation is about the previous scan. Clear it the
    // moment a new one starts, or a scan that goes on to fail leaves a stale
    // "You're up to date" sitting under the loader.
    setEmptyScan(null);
    // The identity this scan belongs to, fixed at the moment it starts. It
    // travels with the scan through the poll and the apply; nothing downstream
    // re-reads `brand` from the closure.
    const epoch = epochRef.current;
    const brandId = brand.brand_id;
    try {
      // Only send platforms when the user actually opened the picker (else let
      // the backend use its smart defaults for this lens).
      const platforms = selectedPlatforms ? Array.from(selectedPlatforms) : undefined;
      // Async: start the scan, then poll — so no single request hits the ~120s
      // gateway timeout.
      const start = await api.post("/scans", { brand_id: brandId, feature, platforms });
      if (!stillOurs(epoch, brandId)) throw TENANT_CHANGED;
      const scanId = start.data?.scan_id;
      setLastScanId(scanId || null);
      setScanStartedAt(Date.now());
      if (!scanId) throw new Error("Scan didn't start. Please try again.");
      const data = await pollScan(scanId, epoch, brandId);
      await applyScanResult(data, feature, { toast: true, epoch, brandId });
    } catch (e) {
      // Silent, and first: the user is looking at a different workspace now.
      // The scan itself is fine — it is still running in the one they left, and
      // its results are waiting there. Saying "Scan failed" here would blame the
      // wrong tenant for something that did not fail.
      if (e === TENANT_CHANGED) return;
      // Any other failure is also only news to the tenant that asked for it.
      if (!stillOurs(epoch, brandId)) return;
      if (isUpgradeError(e)) {
        toast.error(upgradeMessage(e));
        navigate("/app/billing");
      } else if (isQuotaError(e)) {
        setPaywall(extractPaywallInfo(e));
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Scan failed.");
      }
    } finally {
      // The tenant-clear effect already set this false on a switch; a stale
      // continuation must not touch the new tenant's spinner.
      if (stillOurs(epoch, brandId)) setScanning(false);
    }
  };

  const saveLead = async (r) => {
    // A `result_id` belongs to the tenant that produced it. If the tenant moves
    // while the POST is in flight, neither the tick nor the toast belongs on the
    // screen that is now showing — the row it referred to is not even there.
    const epoch = epochRef.current;
    const brandId = brandRef.current?.brand_id;
    setSavingIds((s) => new Set(s).add(r.result_id));
    try {
      await api.post("/leads", { result_id: r.result_id });
      if (!stillOurs(epoch, brandId)) return;
      setSavedResultIds((s) => new Set(s).add(r.result_id));
      toast.success("Saved to your leads.");
    } catch (e) {
      if (!stillOurs(epoch, brandId)) return;
      if (isUpgradeError(e)) {
        toast.error(upgradeMessage(e));
        navigate("/app/billing");
      } else {
        toast.error(e?.response?.data?.detail || "Could not save lead.");
      }
    } finally {
      setSavingIds((s) => {
        const n = new Set(s);
        n.delete(r.result_id);
        return n;
      });
    }
  };


  const dismissLead = (r) => {
    setResults((rs) => rs.filter((x) => x.result_id !== r.result_id));
  };

  const filtered = useMemo(() => {
    return results.filter((r) => {
      if (filterPlatform && r.platform !== filterPlatform) return false;
      if (showPainOnly && !isPainPost(r.post_kind)) return false;
      // Intel/broadcast content is hidden from the main view unless the toggle
      // is on — and when it IS on, show ONLY that content (doc 07).
      if (showIntelOnly ? !r.is_broadcast : r.is_broadcast) return false;
      if (minScore && (r.opportunity_score || 0) < minScore) return false;
      return true;
    });
  }, [results, filterPlatform, showPainOnly, showIntelOnly, minScore]);

  // "New Today" — discovered in the latest scan (doc 07). Pulled into its own
  // band up top; everything else groups/filters as usual below it, so a fresh
  // buyer never gets buried under older, lower-priority signal.
  const showEmptyScanPanel =
    !!emptyScan && emptyScan.feature === activeLens && results.length === 0;

  const newToday = useMemo(() => filtered.filter((r) => r.is_new_today), [filtered]);
  const notNew = useMemo(() => filtered.filter((r) => !r.is_new_today), [filtered]);

  // Posts caught by the Pain & Complaints toggle whose funnel_stage isn't a
  // buying-momentum stage (e.g. "unaware" — venting, no solution in mind yet).
  // These never fit any STAGES bucket below, so without this they'd silently
  // vanish from view the moment the toggle is on — exactly the signal it exists to catch.
  const orphanPainItems = useMemo(() => {
    if (!showPainOnly) return [];
    const staged = new Set(FUNNEL_STAGE_ORDER);
    return notNew.filter((r) => !staged.has(r.funnel_stage));
  }, [notNew, showPainOnly]);

  if (brandsLoading) {
    return <PageLoader />;
  }
  if (!brand) {
    return (
      <RequiresBrand
        title="Import your brand to start finding customers"
        body="Leadmato needs to know what you sell before it can surface high-intent conversations. Import your website URL — takes about 30 seconds."
      >
        {null}
      </RequiresBrand>
    );
  }

  return (
    <div>
      {/* Header row */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-1.5">
            {LENS_FULL_NAME[activeLens] || "Find Customers"} <span className="text-lm-text3">· lens</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
            Real people. Real intent. On demand.
          </h1>
          <p className="text-lm-text2 mt-2 max-w-2xl">
            Every scan sweeps Reddit, forums and more for your buyer keywords, then ranks the best signals —
            grouped by how close they are to buying, below.
          </p>
        </div>
        {/* Run-scan card + meta below it */}
        <div className="shrink-0 w-full md:w-auto md:min-w-[240px]">
          <div className="rounded-xl border border-lm-border bg-lm-surface p-4">
            {!entLoaded ? (
              // Same shape as the real trigger so nothing jumps when it
              // resolves, but neutral — it promises no scan and sells nothing.
              // TWO states, not one: "Checking…" while a fetch is genuinely in
              // flight, "Paused" once we've stopped. The second is the whole
              // point — a scan costs money and consumes quota, and neither runs
              // on an unconfirmed entitlement.
              <button
                data-testid={entUnknown ? "scan-unknown-btn" : "scan-pending-btn"}
                disabled
                aria-busy={entUnknown ? "false" : "true"}
                aria-label={entUnknown ? "Paused. See the message at the top of the page." : undefined}
                className="w-full inline-flex items-center justify-center gap-2 bg-lm-tint text-lm-text3 border border-lm-border rounded-lg px-5 py-3 font-semibold cursor-default"
              >
                <span
                  aria-hidden="true"
                  className={`w-4 h-4 rounded-full bg-lm-text3/25 ${
                    entUnknown ? "" : "animate-pulse motion-reduce:animate-none"
                  }`}
                />
                {entUnknown ? "Paused" : "Checking your plan…"}
              </button>
            ) : (!isPaid && scanUsed) ? (
              <button
                data-testid="scan-upgrade-btn"
                onClick={() => navigate("/app/billing")}
                className="w-full inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-3 font-semibold hover:bg-lm-primaryHover transition-all shadow-[0_6px_18px_-4px_rgba(31,74,56,0.4)]"
              >
                <Sparkles className="w-4 h-4" /> Upgrade to scan
              </button>
            ) : (
              <button
                data-testid={TID.scanTriggerBtn}
                onClick={() => { setScanLens(activeLens); setAskCost(true); }}
                disabled={scanning}
                className="w-full inline-flex items-center justify-center gap-2 bg-lm-secondary text-white rounded-lg px-5 py-3 font-semibold hover:bg-lm-secondaryHover transition-all shadow-[0_6px_18px_-4px_rgba(217,90,64,0.4)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                {scanning ? "Scanning…" : "Run scan"}
                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-white/20">{isPaid ? "1 scan" : freeScansPhrase()}</span>
              </button>
            )}
          </div>
          <div className="mt-2.5 flex flex-col items-center gap-1.5">
            <div
              className="text-xs text-lm-text3 font-mono text-center"
              data-testid={entLoaded ? undefined : entUnknown ? "scan-meta-unknown" : "scan-meta-pending"}
            >
              {/* "One moment…" is a promise of imminent arrival. Once the
                  ladder has stopped it is false, so the slot goes silent —
                  the banner above already carries the explanation (§4). */}
              {/* `?? 0` here used to render "You have 0 scans left" to a paid
                  user during any fetch gap — the one moment they are about to
                  act on it. `scansLeftSentence` returns null when the count is
                  not known, and we render nothing rather than a wrong number. */}
              {!entLoaded
                ? (entUnknown ? "" : "One moment…")
                : isPaid
                  ? (scansLeftSentence(workspace?.usage_today) || "")
                  : scanUsed
                    ? freeScanUsedPhrase()
                    : `${freeScansPhrase()} on us`}
            </div>
          </div>
        </div>
      </div>

      {/* Lens tabs — segmented control (the 4 goals of one scan) */}
      <div className="flex flex-wrap gap-1 rounded-lg bg-lm-tint p-1 mb-6 max-w-2xl shadow-[0_0_0_1px_rgba(0,0,0,0.06)]">
        {LENSES.map((l) => {
          const on = activeLens === l.id;
          const soon = l.comingSoon;
          // Lens tabs are NOT locked for free users — they can browse active lenses
          // (matches the sidebar sub-links). Only Source + Save-lead are gated.
          // Market Insights + Find Partners are coming soon → disabled.
          return (
            <button
              key={l.id}
              disabled={soon}
              onClick={() => { if (!soon) navigate(`/app/find-customers?lens=${l.id}`); }}
              title={soon ? `${l.label} — coming soon` : undefined}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap transition-all ${
                soon
                  ? "text-lm-text3 cursor-not-allowed opacity-70"
                  : on ? "bg-white text-lm-primary shadow-sm" : "text-lm-text2 hover:text-lm-text"
              }`}
            >
              <l.icon className="w-4 h-4" strokeWidth={2.2} /> {l.label}
              {soon && (
                <span className="ml-0.5 rounded-full bg-lm-tint2 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-lm-text3">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {scanSummary && (
        <div className="rounded-xl border border-lm-tint2 bg-lm-tint2/40 p-4 mb-6 flex flex-wrap items-center gap-6 text-sm">
          <div><span className="font-mono font-semibold">{scanSummary.fetched}</span> fetched</div>
          <div><span className="font-mono font-semibold">{scanSummary.new}</span> new</div>
          <div><span className="font-mono font-semibold">{scanSummary.skipped}</span> duplicates skipped</div>
          {lastScanId && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-lm-text3 font-mono truncate max-w-[180px]" title={lastScanId}>
                {lastScanId}
              </span>
              {/* Developer API disabled (coming soon) — hide "Fetch via API".
              <CopyAsCurl
                path={`/scans/${lastScanId}`}
                label="Fetch via API"
                testId="findcustomers-copy-curl"
              /> */}
            </div>
          )}
        </div>
      )}

      <FirstScanSafetyNet
        safetyNet={safetyNet}
        brandId={brand?.brand_id}
        onScopeWidened={async () => {
          setSafetyNet(null);
          await refreshWorkspace();
          runScan();
        }}
        onKeywordAdded={async () => {
          setSafetyNet(null);
          runScan();
        }}
        onDismiss={() => setSafetyNet(null)}
      />

      {/* Filters — modern toolbar */}
      <div className="rounded-xl border border-lm-border bg-lm-surface p-2.5 mb-6 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-lm-text3 px-2">
          <Filter className="w-3.5 h-3.5" /> Filters
        </span>
        <span className="h-6 w-px bg-lm-border hidden sm:block" />

        {/* Platform */}
        <label className="inline-flex items-center gap-2 rounded-lg bg-lm-tint/50 hover:bg-lm-tint transition-colors pl-3 pr-1.5 py-1">
          <span className="text-xs text-lm-text3">Platform</span>
          <select
            data-testid={TID.filterPlatform}
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            className="bg-transparent text-sm font-medium text-lm-text focus:outline-none cursor-pointer"
          >
            <option value="">All</option>
            <option value="reddit">Reddit</option>
            <option value="forums">Forums</option>
            <option value="linkedin">LinkedIn</option>
            <option value="youtube">YouTube</option>
            <option value="instagram">Instagram</option>
            <option value="web">Web</option>
          </select>
        </label>

        {/* Pain & Complaints — Pain Points folded into Find Customers as a toggle,
            not a separate lens/scan. Surfaces venting/complaint posts, including
            ones too early-stage (funnel_stage=unaware) to appear in any stage group below. */}
        <button
          type="button"
          data-testid={TID.filterPainToggle}
          onClick={() => { setShowPainOnly((v) => !v); setShowIntelOnly(false); }}
          aria-pressed={showPainOnly}
          title="Show only posts where someone is venting or complaining"
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all border ${
            showPainOnly
              ? "bg-orange-50 border-orange-300 text-orange-700 shadow-[0_0_0_1px_rgba(251,146,60,0.35)]"
              : "bg-lm-tint/50 border-transparent hover:bg-lm-tint text-lm-text2"
          }`}
        >
          <HeartPulse className={`w-3.5 h-3.5 ${showPainOnly ? "text-orange-600" : "text-lm-text3"}`} />
          <span>Pain &amp; Complaints</span>
        </button>

        {/* Intel — creator/business content + directory listings, not a 1:1 buyer
            conversation. Hidden from the main view by default so it never dilutes
            the hot-leads list (doc 07); this toggle is the only way to see it. */}
        <button
          type="button"
          data-testid={TID.filterIntelToggle}
          onClick={() => { setShowIntelOnly((v) => !v); setShowPainOnly(false); }}
          aria-pressed={showIntelOnly}
          title="Show broadcast/creator content — not 1:1 buyer conversations, but useful market intel"
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all border ${
            showIntelOnly
              ? "bg-slate-100 border-slate-300 text-slate-700 shadow-[0_0_0_1px_rgba(100,116,139,0.35)]"
              : "bg-lm-tint/50 border-transparent hover:bg-lm-tint text-lm-text2"
          }`}
        >
          <Radio className={`w-3.5 h-3.5 ${showIntelOnly ? "text-slate-600" : "text-lm-text3"}`} />
          <span>Intel</span>
        </button>

        {/* Min score */}
        <div className="inline-flex items-center gap-2.5 rounded-lg bg-lm-tint/50 px-3 py-1.5">
          <span className="text-xs text-lm-text3 whitespace-nowrap">Min score</span>
          <input
            data-testid={TID.filterScore}
            type="range"
            min={0}
            max={100}
            step={5}
            value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value))}
            className="accent-lm-primary w-24"
          />
          <span className="font-mono text-xs font-semibold text-lm-primary bg-white rounded px-1.5 py-0.5 min-w-[28px] text-center tabular-nums">{minScore}</span>
        </div>

        <div className="ml-auto flex items-center gap-2 px-1">
          <span className="text-xs font-mono text-lm-text3 hidden sm:inline">{filtered.length} shown</span>
          {(filterPlatform || showPainOnly || showIntelOnly || minScore > 0) && (
            <button
              onClick={() => { setFilterPlatform(""); setShowPainOnly(false); setShowIntelOnly(false); setMinScore(0); }}
              className="inline-flex items-center gap-1.5 text-sm text-lm-text2 hover:text-red-600 px-2.5 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
            >
              <X className="w-4 h-4" /> Reset
            </button>
          )}
          <button
            onClick={() => fetchResults(brand)}
            className="inline-flex items-center gap-1.5 text-sm text-lm-text2 hover:text-lm-text px-2.5 py-1.5 rounded-lg hover:bg-lm-tint"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Body */}
      {resultsError ? (
        <ErrorPanel message={resultsError} onRetry={() => fetchResults(brand)} />
      ) : scanning ? (
        <ScanLoader startedAt={scanStartedAt} />
      ) : resultsLoading ? (
        <LoadingGrid />
      ) : filtered.length === 0 ? (
        // The empty-scan explanation only speaks for the lens it ran on, and only
        // when the lens genuinely has nothing — `results.length > 0` with
        // `filtered.length === 0` means the user's own filters emptied the view,
        // and "we read 340 posts and none were customers" would then be false.
        showEmptyScanPanel ? (
          <EmptyScanPanel
            reason={emptyScan.reason}
            count={emptyScan.count}
            refunded={emptyScan.refunded}
            refundDeclined={emptyScan.refundDeclined}
            refundsPerDay={emptyScan.refundsPerDay}
          />
        ) : (
          <EmptyPanel onRunScan={() => { setScanLens(activeLens); setAskCost(true); }} />
        )
      ) : (
        <div>
          {/* New Today — discovered in the latest scan, pinned above everything
              else so a fresh buyer can never get buried (doc 07). Priority
              within this band is still the rank order the backend already sorted. */}
          {newToday.length > 0 && (
            <div className="mb-8">
              <div className="w-full flex items-center gap-2.5 mb-3 px-4 py-3.5 rounded-xl bg-lm-secondary/10 border border-lm-secondary/30">
                <Flame className="w-4 h-4 text-lm-secondary" />
                <h3 className="font-display font-semibold text-base">New today</h3>
                <InfoTip text="Discovered in your latest scan — never shown before." />
                <span className="ml-auto text-xs font-mono font-semibold text-lm-secondary bg-lm-surface border border-lm-border rounded-full px-2 py-0.5">{newToday.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {newToday.map((r) => (
                  <LeadCard
                    key={r.result_id}
                    result={r}
                    saved={savedResultIds.has(r.result_id)}
                    saving={savingIds.has(r.result_id)}
                    onSave={() => saveLead(r)}
                    onDismiss={() => dismissLead(r)}
                  />
                ))}
              </div>
            </div>
          )}

          {showIntelOnly ? (
            // Intel content isn't buyer-funnel shaped — one flat list, not
            // grouped by funnel stage.
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {notNew.map((r) => (
                <LeadCard
                  key={r.result_id}
                  result={r}
                  saved={savedResultIds.has(r.result_id)}
                  saving={savingIds.has(r.result_id)}
                  onSave={() => saveLead(r)}
                  onDismiss={() => dismissLead(r)}
                />
              ))}
            </div>
          ) : (
            <>
              {STAGES.map((s) => {
                const items = notNew.filter((r) => r.funnel_stage === s.id);
                if (!items.length) return null;
                const isCollapsed = !!collapsed[s.id];
                return (
                  <div key={s.id} className="mb-8">
                    <button
                      type="button"
                      onClick={() => setCollapsed((c) => ({ ...c, [s.id]: !c[s.id] }))}
                      className="w-full flex items-center gap-2.5 mb-3 px-4 py-3.5 rounded-xl bg-lm-tint/60 border border-lm-border hover:bg-lm-tint transition-colors group"
                    >
                      <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                      <h3 className="font-display font-semibold text-base">{s.label}</h3>
                      <InfoTip text={s.hint} />
                      <span className="ml-auto text-xs font-mono font-semibold text-lm-text2 bg-lm-surface border border-lm-border rounded-full px-2 py-0.5">{items.length}</span>
                      <ChevronDown className={`w-4 h-4 text-lm-text3 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                    </button>
                    {!isCollapsed && (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {items.map((r) => (
                          <LeadCard
                            key={r.result_id}
                            result={r}
                            saved={savedResultIds.has(r.result_id)}
                            saving={savingIds.has(r.result_id)}
                            onSave={() => saveLead(r)}
                            onDismiss={() => dismissLead(r)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {orphanPainItems.length > 0 && (
                <div className="mb-8">
                  <div className="w-full flex items-center gap-2.5 mb-3 px-4 py-3.5 rounded-xl bg-orange-50 border border-orange-200">
                    <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                    <h3 className="font-display font-semibold text-base">Frustrated, not shopping yet</h3>
                    <InfoTip text="Venting about a problem — no buying signal yet, but worth knowing about." />
                    <span className="ml-auto text-xs font-mono font-semibold text-lm-text2 bg-lm-surface border border-lm-border rounded-full px-2 py-0.5">{orphanPainItems.length}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {orphanPainItems.map((r) => (
                      <LeadCard
                        key={r.result_id}
                        result={r}
                        saved={savedResultIds.has(r.result_id)}
                        saving={savingIds.has(r.result_id)}
                        onSave={() => saveLead(r)}
                        onDismiss={() => dismissLead(r)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <ScanConfirmModal
        open={askCost}
        lens={scanLens}
        setLens={setScanLens}
        keywords={activeKeywords}
        brand={brand}
        usage={workspace?.usage_today}
        scanning={scanning}
        platformOpts={platformOpts}
        selectedPlatforms={selectedPlatforms}
        togglePlatform={togglePlatform}
        maxPlatforms={MAX_PLATFORMS}
        onCancel={() => setAskCost(false)}
        onConfirm={() => runScan(scanLens)}
      />
      <PaywallModal
        open={!!paywall}
        info={paywall}
        onClose={() => setPaywall(null)}
      />
    </div>
  );
}

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-lm-primary mx-auto mb-3" />
        <div className="text-lm-text2 text-sm">Loading your workspace…</div>
      </div>
    </div>
  );
}

function LoadingGrid() {
  return (
    <div
      data-testid={TID.scanRunningLoader}
      className="grid grid-cols-1 lg:grid-cols-2 gap-5"
    >
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-lm-surface border border-lm-border rounded-xl p-6 lm-pulse-warm">
          <div className="h-4 w-24 rounded bg-lm-tint mb-3" />
          <div className="h-3 w-full rounded bg-lm-tint mb-2" />
          <div className="h-3 w-4/5 rounded bg-lm-tint mb-2" />
          <div className="h-3 w-3/5 rounded bg-lm-tint" />
        </div>
      ))}
    </div>
  );
}

function EmptyPanel({ onRunScan }) {
  return (
    <div
      data-testid={TID.emptyStateFindCustomers}
      className="rounded-2xl border-2 border-dashed border-lm-border bg-lm-surface p-12 text-center"
    >
      <div className="w-14 h-14 rounded-2xl bg-lm-tint2 flex items-center justify-center mx-auto mb-5">
        <Inbox className="w-6 h-6 text-lm-primary" />
      </div>
      {/* No terminal period — headings aren't sentences, and this one shares a
          screen with the three empty-scan headlines, which have none. */}
      <h3 className="text-xl font-display font-semibold mb-2">No leads yet</h3>
      <p className="text-lm-text2 max-w-md mx-auto mb-6">
        Run your first scan to sweep the social web for people talking about the problems you solve.
      </p>
      <button
        onClick={onRunScan}
        className="inline-flex items-center gap-2 bg-lm-secondary text-white rounded-lg px-5 py-3 font-medium hover:bg-lm-secondaryHover"
      >
        <Sparkles className="w-4 h-4" /> Run scan · 1 scan
      </button>
    </div>
  );
}

function formatScope(scope) {
  switch (scope) {
    case "local_city": return "My city";
    case "india": return "All India";
    case "us": return "United States";
    case "global": return "Global";
    default: return "Not set";
  }
}

function InfoTip({ text }) {
  return (
    <span className="relative inline-flex items-center group/tip" tabIndex={0}>
      <Info className="w-3.5 h-3.5 text-lm-text3 cursor-help hover:text-lm-primary transition-colors" />
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 w-max max-w-[240px] rounded-md bg-[color:var(--lm-dark)] text-white text-xs leading-snug px-2.5 py-1.5 text-center normal-case tracking-normal font-sans opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible group-focus/tip:opacity-100 group-focus/tip:visible transition-opacity"
      >
        {text}
      </span>
    </span>
  );
}

function ScanConfirmModal({ open, lens, setLens, keywords, brand, usage, scanning,
                            platformOpts = [], selectedPlatforms, togglePlatform, maxPlatforms = 5,
                            onCancel, onConfirm }) {
  if (!open) return null;
  const selected = selectedPlatforms || new Set();
  const noPlatforms = selected.size === 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={scanning ? undefined : onCancel}>
      <div
        className="w-full max-w-lg rounded-2xl bg-lm-surface border border-lm-border shadow-2xl p-6 animate-[fadeIn_0.2s_ease] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-11 h-11 rounded-xl bg-lm-tint2 flex items-center justify-center mb-4">
          <PlayCircle className="w-5 h-5 text-lm-primary" />
        </div>
        <h3 className="text-xl font-display font-semibold mb-1">Run a scan?</h3>
        <p className="text-sm text-lm-text2 mb-5">Pick the goal, review what we'll search, then go.</p>

        {/* Lens picker */}
        <div className="mb-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3 mb-2">Goal / lens</div>
          <div className="flex flex-wrap gap-2">
            {LENSES.map((l) => {
              const on = lens === l.id;
              const soon = l.comingSoon;
              return (
                <button
                  key={l.id}
                  disabled={soon}
                  onClick={() => { if (!soon) setLens(l.id); }}
                  title={soon ? `${l.label} — coming soon` : undefined}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    soon
                      ? "bg-lm-bg text-lm-text3 border-lm-border cursor-not-allowed opacity-70"
                      : on ? "bg-lm-primary text-white border-lm-primary" : "bg-white text-lm-text2 border-lm-border hover:border-lm-primary/50 hover:text-lm-text"
                  }`}
                >
                  {l.emoji} {l.label}{soon && <span className="text-[9px] font-mono uppercase">· soon</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Keywords */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-lm-text3">
              Keywords we'll search ({keywords.length})
              <InfoTip text="Each keyword is searched separately — more keywords = more thorough, but a longer scan (~a few seconds each). Tick fewer for a faster run." />
            </div>
            <Link to="/app/brand" className="text-xs text-lm-primary hover:underline">Edit in Brand</Link>
          </div>
          {keywords.length === 0 ? (
            <div className="text-sm text-lm-text3">No active keywords — tick some in Brand Foundation first.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto rounded-lg bg-lm-bg/60 border border-lm-border p-2.5">
              {keywords.map((k, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full bg-lm-tint2 text-lm-primary text-xs">{k.text}</span>
              ))}
            </div>
          )}
        </div>

        {/* Platforms — suggested pre-ticked, editable, max 5 */}
        <div className="mb-5">
          <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-lm-text3 mb-2">
            Platforms to search ({selected.size}/{maxPlatforms})
            <InfoTip text="Pick up to 5 platforms per scan — more platforms makes a scan much slower. We pre-tick the best ones for this lens; tick or untick any." />
          </div>
          {platformOpts.length === 0 ? (
            <div className="text-sm text-lm-text3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading platforms…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {platformOpts.map((o) => {
                const on = selected.has(o.id);
                const atCap = !on && selected.size >= maxPlatforms;
                return (
                  <button
                    key={o.id}
                    type="button"
                    disabled={atCap}
                    onClick={() => togglePlatform(o.id)}
                    title={atCap ? "Up to 5 platforms per scan — untick one first." : undefined}
                    className={`inline-flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs transition-colors ${
                      on
                        ? "bg-lm-tint2 border-lm-primary text-lm-text"
                        : atCap
                        ? "bg-lm-bg border-lm-border text-lm-text3/50 cursor-not-allowed"
                        : "bg-white border-lm-border text-lm-text3 hover:border-lm-primary/40"
                    }`}
                  >
                    <SocialLogo platform={o.id} className="w-4 h-4 shrink-0" />
                    <span className="truncate">{PLATFORM_NAMES[o.id] || o.id}</span>
                    {on && <Check className="w-3.5 h-3.5 ml-auto text-lm-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
          {noPlatforms && platformOpts.length > 0 ? (
            <div className="text-xs text-lm-secondary mt-2">Pick at least one platform to run.</div>
          ) : selected.size >= maxPlatforms ? (
            <div className="text-xs text-lm-text3 mt-2">Max {maxPlatforms} platforms per scan — untick one to swap.</div>
          ) : null}
        </div>

        {/* Scope + cost */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-lm-text3 mb-6 pt-3 border-t border-lm-border">
          <span>Scope: <span className="text-lm-text2 font-medium">{formatScope(brand?.market_scope)}</span>{brand?.market_city ? ` · ${brand.market_city}` : ""}</span>
          {/* Was "{n} left today" unconditionally, fed a `?? 0` default — so a
              monthly allowance was labelled as a daily one, and a fetch gap
              read "0 left today" on the confirm step itself. */}
          <span>
            <span className="font-mono text-lm-secondary">1 scan</span>
            {(() => {
              const r = scansRemaining(usage);
              if (!r.known) return null;
              return ` · ${r.remaining} left${r.period ? ` ${r.period}` : ""}`;
            })()}
          </span>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button onClick={onCancel} disabled={scanning} className="px-4 py-2.5 rounded-lg text-sm font-medium text-lm-text2 hover:bg-lm-tint/60 disabled:opacity-60">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={scanning || keywords.length === 0 || noPlatforms}
            className="inline-flex items-center gap-2 bg-lm-secondary text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-lm-secondaryHover disabled:opacity-60"
          >
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            {scanning ? "Scanning…" : "Run scan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div
      data-testid={TID.errorStateFindCustomers}
      className="rounded-2xl border border-red-200 bg-red-50/60 p-8 text-center"
    >
      <div className="w-12 h-12 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-semibold mb-1">Something went wrong.</h3>
      <p className="text-lm-text2 text-sm mb-5">{message}</p>
      <button onClick={onRetry} className="inline-flex items-center gap-1.5 border border-lm-border bg-white rounded-lg px-4 py-2 text-sm hover:bg-lm-tint">
        <RefreshCw className="w-4 h-4" /> Retry
      </button>
    </div>
  );
}
